import { useState, useRef, ChangeEvent, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Upload, X, Star, GripVertical, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { uploadMultipleImages, deleteImage } from "@/utils/uploadUtils";
import { convertHeicFiles, hasAnyHeic, formatHeicErrorMessage } from "@/utils/heicConversion";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const MAX_IMAGES = 90;

interface PropertyImageManagerProps {
  propertyId: string;
  currentImages: Array<{
    id: string;
    image_url: string;
    is_primary: boolean;
    display_order: number;
  }>;
  onImagesUpdated: () => void;
}

interface SortableImageItemProps {
  image: {
    id: string;
    image_url: string;
    is_primary: boolean;
    display_order: number;
  };
  onSetPrimary: (imageId: string, imageUrl: string) => void;
  onDelete: (imageId: string, imageUrl: string) => void;
  isDeleting: boolean;
}

const SortableImageItem = ({ image, onSetPrimary, onDelete, isDeleting }: SortableImageItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`relative aspect-square bg-muted rounded-md overflow-hidden group ${
        isDragging ? 'z-50 shadow-2xl ring-2 ring-godyspo-gold' : ''
      }`}
    >
      {/* Drag Handle */}
      <div 
        {...attributes}
        {...listeners}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/70 p-2 rounded-full cursor-move opacity-0 group-hover:opacity-100 transition-opacity z-10"
        title="Drag to reorder"
      >
        <GripVertical className="h-5 w-5 text-white" />
      </div>
      
      {/* Image */}
      <img 
        src={image.image_url} 
        alt="Property image" 
        className="w-full h-full object-cover"
      />
      
      {/* Primary Badge */}
      {image.is_primary && (
        <div className="absolute top-1 left-1 bg-godyspo-gold text-black text-xs px-2 py-1 rounded font-medium">
          Primary
        </div>
      )}
      
      {/* Action Buttons */}
      <div className="absolute top-1 right-1 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {!image.is_primary && (
          <button
            type="button"
            onClick={() => onSetPrimary(image.id, image.image_url)}
            className="bg-black/50 hover:bg-black/70 text-white p-1 rounded-full"
            title="Set as primary image"
          >
            <Star className="h-3 w-3" />
          </button>
        )}
        <button
          type="button"
          onClick={() => onDelete(image.id, image.image_url)}
          disabled={isDeleting}
          className="bg-black/50 hover:bg-black/70 text-white p-1 rounded-full disabled:opacity-50"
          title="Delete image"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
      
      {/* Deleting Overlay */}
      {isDeleting && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <div className="text-white text-xs">Deleting...</div>
        </div>
      )}
    </div>
  );
};

export const PropertyImageManager = ({
  propertyId,
  currentImages,
  onImagesUpdated
}: PropertyImageManagerProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ completed: number; total: number } | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [orderedImages, setOrderedImages] = useState(currentImages);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Refresh guard while an upload is in flight
  useEffect(() => {
    if (!isUploading) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isUploading]);


  // Update orderedImages when currentImages changes
  useEffect(() => {
    setOrderedImages([...currentImages].sort((a, b) => a.display_order - b.display_order));
  }, [currentImages]);

  // Setup drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required to start drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const oldIndex = orderedImages.findIndex(img => img.id === active.id);
    const newIndex = orderedImages.findIndex(img => img.id === over.id);
    
    // Optimistically update UI
    const newOrder = arrayMove(orderedImages, oldIndex, newIndex);
    setOrderedImages(newOrder);
    
    try {
      // Update display_order in database for all affected images
      const updates = newOrder.map((image, index) => ({
        id: image.id,
        property_id: propertyId,
        image_url: image.image_url,
        is_primary: image.is_primary,
        display_order: index + 1
      }));
      
      // Batch update all images
      const { error } = await supabase
        .from('property_images')
        .upsert(updates, { onConflict: 'id' });
      
      if (error) throw error;
      
      toast({
        title: "Order updated",
        description: "Image order has been saved"
      });
      
      // No need to refresh - optimistic update already shows correct order
      
    } catch (error) {
      console.error("Error updating image order:", error);
      
      // Revert on error
      setOrderedImages(currentImages);
      
      toast({
        title: "Update failed",
        description: "Failed to save new image order",
        variant: "destructive"
      });
    }
  };

  const handleImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    let newFiles = Array.from(e.target.files);

    // Transparently convert iPhone HEIC/HEIF photos to JPEG.
    if (hasAnyHeic(newFiles)) {
      toast({
        title: "Converting iPhone photos…",
        description: "HEIC images are being converted to JPEG.",
      });
      try {
        const { files: converted, errors } = await convertHeicFiles(newFiles);
        newFiles = converted;
        if (errors.length > 0) {
          toast({
            title: "Some HEIC files could not be converted",
            description: formatHeicErrorMessage(errors),
            variant: "destructive",
          });
        }
        if (newFiles.length === 0) {
          if (fileInputRef.current) fileInputRef.current.value = "";
          return;
        }
      } catch (err) {
        console.error("HEIC conversion failed:", err);
        toast({
          title: "HEIC conversion failed",
          description: "Please convert your photos to JPEG and try again.",
          variant: "destructive",
        });
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
    }

    const totalImagesAfterUpload = orderedImages.length + newFiles.length;
    
    // Check if adding these images would exceed the limit
    if (totalImagesAfterUpload > MAX_IMAGES) {
      const remainingSlots = MAX_IMAGES - orderedImages.length;
      toast({
        title: "Too many images",
        description: `You can only upload ${remainingSlots} more image${remainingSlots !== 1 ? 's' : ''}. Maximum ${MAX_IMAGES} images per listing.`,
        variant: "destructive"
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    
    setIsUploading(true);
    setUploadProgress({ completed: 0, total: newFiles.length });
    
    try {
      // Upload images to storage in batches with progress tracking
      const folderPath = `property-${propertyId}`;
      const uploadedUrls = await uploadMultipleImages(
        newFiles, 
        'properties', 
        folderPath,
        5, // Batch size of 5 images
        (completed, total) => {
          setUploadProgress({ completed, total });
        }
      );

      
      if (uploadedUrls.length > 0) {
        // Insert new images into property_images table
        const imageRecords = uploadedUrls.map((url, index) => ({
          property_id: propertyId,
          image_url: url,
          is_primary: false,
          display_order: orderedImages.length + index + 1
        }));
        
        const { error } = await supabase
          .from('property_images')
          .insert(imageRecords);
        
        if (error) {
          throw new Error(error.message);
        }
        
        // If this is the first image and there's no primary image, update the main property record
        if (orderedImages.length === 0) {
          const { error: updateError } = await supabase
            .from('properties')
            .update({ image_url: uploadedUrls[0] })
            .eq('id', propertyId);
          
          if (updateError) {
            console.error("Error updating main property image:", updateError);
          }
        }
        
        toast({
          title: "Images uploaded",
          description: `Successfully uploaded ${uploadedUrls.length} images`
        });
        
        onImagesUpdated();
      } else {
        throw new Error("Failed to upload images");
      }
    } catch (error) {
      console.error("Error uploading images:", error);
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Failed to upload images",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }

  };

  const handleDeleteImage = async (imageId: string, imageUrl: string) => {
    if (!confirm("Are you sure you want to delete this image?")) return;
    
    setIsDeleting(imageId);
    
    try {
      // Delete from storage
      const deleteSuccess = await deleteImage(imageUrl);
      
      if (!deleteSuccess) {
        console.warn("Failed to delete image from storage, but continuing with database deletion");
      }
      
      // Delete from database
      const { error } = await supabase
        .from('property_images')
        .delete()
        .eq('id', imageId);
      
      if (error) {
        throw new Error(error.message);
      }
      
      // If this was the primary image, update the main property record
      const deletedImage = currentImages.find(img => img.id === imageId);
      if (deletedImage?.is_primary) {
        const remainingImages = currentImages.filter(img => img.id !== imageId);
        const newPrimaryUrl = remainingImages.length > 0 ? remainingImages[0].image_url : null;
        
        const { error: updateError } = await supabase
          .from('properties')
          .update({ image_url: newPrimaryUrl })
          .eq('id', propertyId);
        
        if (updateError) {
          console.error("Error updating main property image:", updateError);
        }
      }
      
      toast({
        title: "Image deleted",
        description: "Image has been removed from the listing"
      });
      
      onImagesUpdated();
    } catch (error) {
      console.error("Error deleting image:", error);
      toast({
        title: "Delete failed",
        description: error instanceof Error ? error.message : "Failed to delete image",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const handleSetPrimary = async (imageId: string, imageUrl: string) => {
    // Store original state for rollback
    const previousImages = [...orderedImages];
    
    // Optimistic update - immediately show new primary
    setOrderedImages(prev => prev.map(img => ({
      ...img,
      is_primary: img.id === imageId
    })));
    
    try {
      // Update all images to not be primary
      const { error: resetError } = await supabase
        .from('property_images')
        .update({ is_primary: false })
        .eq('property_id', propertyId);
      
      if (resetError) throw new Error(resetError.message);
      
      // Set the selected image as primary
      const { error: setPrimaryError } = await supabase
        .from('property_images')
        .update({ is_primary: true })
        .eq('id', imageId);
      
      if (setPrimaryError) throw new Error(setPrimaryError.message);
      
      // Update the main property record
      const { error: updateError } = await supabase
        .from('properties')
        .update({ image_url: imageUrl })
        .eq('id', propertyId);
      
      if (updateError) throw new Error(updateError.message);
      
      toast({
        title: "Primary image updated",
        description: "This image is now the main listing photo"
      });
      
      // No need to refresh - optimistic update already shows correct state
    } catch (error) {
      console.error("Error setting primary image:", error);
      
      // Revert on error
      setOrderedImages(previousImages);
      
      toast({
        title: "Update failed",
        description: error instanceof Error ? error.message : "Failed to update primary image",
        variant: "destructive"
      });
    }
  };

  const handleSaveAndReturn = () => {
    toast({
      title: "Images saved",
      description: "Property images have been updated successfully"
    });
    navigate(`/seller/properties/${propertyId}`);
  };

  const remainingSlots = MAX_IMAGES - orderedImages.length;

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Property Images</h2>
            <div className="text-sm text-muted-foreground">
              {orderedImages.length} / {MAX_IMAGES} images
            </div>
          </div>
          <Separator />
          
          {/* Current Images */}
          {orderedImages.length > 0 && (
            <div>
              <h3 className="font-medium mb-3">
                Current Images ({orderedImages.length})
                <span className="text-xs text-muted-foreground ml-2">
                  Drag to reorder
                </span>
              </h3>
              
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={orderedImages.map(img => img.id)}
                  strategy={rectSortingStrategy}
                >
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {orderedImages.map((image) => (
                      <SortableImageItem
                        key={image.id}
                        image={image}
                        onSetPrimary={handleSetPrimary}
                        onDelete={(id, url) => handleDeleteImage(id, url)}
                        isDeleting={isDeleting === image.id}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          )}
          
          
          {/* Persistent upload progress panel */}
          {isUploading && uploadProgress && (
            <div className="rounded-md border-2 border-godyspo-gold bg-godyspo-gold/10 p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-godyspo-gold" />
                <div className="flex-1">
                  <p className="font-medium text-foreground">
                    Uploading {uploadProgress.completed} of {uploadProgress.total} photos…
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Please keep this tab open. Do not refresh or navigate away.
                  </p>
                </div>
              </div>
              <Progress
                value={uploadProgress.total > 0 ? (uploadProgress.completed / uploadProgress.total) * 100 : 0}
              />
            </div>
          )}

          {/* Upload new images */}

          {remainingSlots > 0 ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">Add New Images</h3>
                <span className="text-sm text-muted-foreground">
                  {remainingSlots} slot{remainingSlots !== 1 ? 's' : ''} remaining
                </span>
              </div>
              <div className="border-2 border-dashed border-border rounded-md p-6 text-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                  disabled={isUploading}
                />
                
                <div className="flex flex-col items-center justify-center">
                  <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
                  <div className="flex text-sm text-muted-foreground">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      className="relative rounded-md font-medium text-godyspo-blue hover:text-godyspo-blue/90 disabled:opacity-50"
                    >
                      {isUploading ? "Uploading..." : "Upload files"}
                    </button>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    PNG, JPG, GIF up to 10MB each • Max {MAX_IMAGES} images per listing
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="border-2 border-dashed border-border rounded-md p-6 text-center bg-muted/50">
              <div className="flex flex-col items-center justify-center">
                <Upload className="mx-auto h-12 w-12 text-muted-foreground opacity-50" />
                <p className="text-sm text-muted-foreground mt-2">
                  Maximum of {MAX_IMAGES} images reached
                </p>
                <p className="text-xs text-muted-foreground">
                  Delete existing images to upload new ones
                </p>
              </div>
            </div>
          )}
          
          {orderedImages.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <p>No images uploaded yet. Add some images to showcase your property.</p>
            </div>
          )}

          {/* Save button at bottom right */}
          <div className="flex justify-end pt-4">
            <Button
              onClick={handleSaveAndReturn}
              className="bg-godyspo-gold hover:bg-godyspo-gold/90 text-black"
            >
              Save Images
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
